import { HStack, Text, Center, FlatList, Spinner, Box, Checkbox, Button } from "native-base";
import { Link } from "react-router-dom";
import colors from "theme/colors";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { getName, numberWithDotsAndNegativeCase, stringLimit } from "utils/helpers";
import { CREATE_MODALS, STATUS_TYPES } from "global/constants";
import { AiOutlineDownload } from "react-icons/ai";
import { getStore } from "store/storeUtils";

const Cell = ({
  children,
  header = false,
  style = {},
  isPressable = false,
  isDownloadButton = false,
  linkTo = "",
  data = "",
  color = "",
  userEmail = "",
}) => {
  const tableCellWidth = "185px";
  const { modal, setModal } = getStore();

  const cellData = {
    data,
  };

  const onSingleRequestDownloadPress = () => {
    setModal({
      ...modal,
      type: CREATE_MODALS.exportSDDataDitolak,
      data: {
        email: userEmail,
        requestId: data,
        status: "success",
      },
    });
  };

  return (
    <Center bg={header ? colors.neutral.mist : colors.neutral.cotton} py="10px">
      {isPressable ? (
        <Box maxW={tableCellWidth} alignItems="center">
          <Link
            to={linkTo}
            state={cellData}
            style={{
              color: colors.secondary.orchidBlue,
              display: "block",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            <Text
              variant={header ? "xs-bold" : "xs"}
              maxW={tableCellWidth}
              color={colors.secondary.orchidBlue}
              style={[style || {}, { textAlign: "center" }]}
            >
              {children}
            </Text>
          </Link>
        </Box>
      ) : isDownloadButton ? (
        <Button
          variant="unstyled"
          onPress={() => onSingleRequestDownloadPress()}
          minW={tableCellWidth}
          style={[style || {}, { alignItems: "center" }]}
        >
          {children}
        </Button>
      ) : (
        <Text
          variant={header ? "xs-bold" : "xs"}
          minW={tableCellWidth}
          style={[style || {}, { textAlign: "center" }]}
          color={color}
        >
          {children}
        </Text>
      )}
    </Center>
  );
};

const Header = ({
  headers,
  showCheckboxes = false,
  setIsAllRowsSelected = () => {},
  isAllRowSelected = false,
  setSelectedRows = () => {},
  setExcludedRows = () => {},
}) => {
  const { t } = useTranslation("common");

  const border = { borderRightWidth: 2, borderColor: "#c1c1c1" };

  const onChangeAllRowSelect = (value) => {
    setIsAllRowsSelected(value);
    if (!value) {
      setSelectedRows([]);
    } else {
      setExcludedRows([]);
    }
  };

  return (
    <HStack>
      {headers.map((header, index) => (
        <Cell
          header
          style={{
            ...border,
            minWidth: index === 0 && !showCheckboxes ? "75px" : index === 0 && showCheckboxes ? "100px" : "200px",
            ...(index === headers.length - 1 && { borderRightWidth: 0 }),
          }}
        >
          {showCheckboxes && index === 0 ? (
            <Center flexDirection="row" px="10px">
              <Checkbox value="select all" isChecked={isAllRowSelected} onChange={onChangeAllRowSelect} />
              <Text variant="xs-bold" style={{ textAlign: "center", paddingLeft: "25px" }}>
                {t(header)}
              </Text>
            </Center>
          ) : (
            <Text variant="xs-bold" style={{ textAlign: "center" }}>
              {t(header)}
            </Text>
          )}
        </Cell>
      ))}
    </HStack>
  );
};

const fieldsForDateConversion = ["createAt", "createdAt", "expectedDisbursementDate"];

const fieldsForTrailingDots = [
  "groupName",
  "transactionNote",
  "rejectionReason",
  "employeeName",
  "beneficiaryName",
  "bankName",
];

const Row = ({
  row = {},
  fields,
  isPressable = false,
  redirectTo = "/",
  showCheckboxes = false,
  selectedRows = [],
  setSelectedRows = () => {},
  rowIndex = 0,
  skip = 0,
  isAllRowSelected = false,
  excludedRows = [],
  setExcludedRows = () => {},
}) => {
  const { t } = useTranslation("common");

  return (
    <HStack zIndex={2}>
      {fields.map((field, index) => {
        const selectRow = (data, value) => {
          if (isAllRowSelected) {
            const updatedRows = !value
              ? [...excludedRows, data]
              : excludedRows.filter((selected) => selected?.id !== data?.id);
            setExcludedRows(updatedRows);
          } else {
            const updatedRows = value
              ? [...selectedRows, data]
              : selectedRows.filter((selected) => selected?.id !== data?.id);
            setSelectedRows(updatedRows);
          }
        };

        const isRowSelected = !isAllRowSelected
          ? selectedRows.some((selectedRow) => selectedRow.id === row?.id)
          : !excludedRows.some((selectedRow) => selectedRow.id === row?.id);

        return (
          <Cell
            isPressable={index === 1 && isPressable} // Assuming the second field is pressable
            linkTo={redirectTo}
            data={row?.id}
            showCheckboxes={showCheckboxes && index === 0}
            isDownloadButton={field === "download"}
            color={field.includes("Status") ? getName(row[field], STATUS_TYPES[field])?.color : ""}
            style={{
              minWidth: index === 0 && !showCheckboxes ? "75px" : index === 0 && showCheckboxes ? "100px" : "200px",
            }}
          >
            {showCheckboxes && index === 0 ? (
              <Center flexDirection="row" px="10px">
                <Checkbox value={row?.id} isChecked={isRowSelected} onChange={(value) => selectRow(row, value)} />
                <Text variant="xs-bold" style={{ textAlign: "center", paddingLeft: "40px" }}>
                  {field === "no" ? `${skip + rowIndex + 1}` : row[field]}
                </Text>
              </Center>
            ) : fieldsForDateConversion.includes(field) ? (
              dayjs(row[field]).format("DD-MM-YYYY")
            ) : field === "no" ? (
              `${skip + rowIndex + 1}`
            ) : field === "amount" ? (
              numberWithDotsAndNegativeCase(row[field])
            ) : field === "staticErrorMessage" ? (
              stringLimit(t("inProgress.downloadDataAndCheckForErrors"))
            ) : field.includes("Status") ? (
              getName(row[field], STATUS_TYPES[field])?.name || ""
            ) : field === "download" ? (
              <AiOutlineDownload size="24px" color={colors.secondary.orchid} />
            ) : fieldsForTrailingDots.includes(field) ? (
              stringLimit(t(row[field]), 20)
            ) : (
              t(row[field])
            )}
          </Cell>
        );
      })}
    </HStack>
  );
};

const CustomTable = ({
  data = [],
  isLoading = false,
  headers = [],
  fields = [],
  isPressable = false,
  redirectTo = "/",
  showCheckboxes = false,
  selectedRows = [],
  setSelectedRows = () => {},
  skip = 0,
  isAllRowSelected = false,
  setIsAllRowSelected = () => {},
  excludedRows = [],
  setExcludedRows = () => {},
}) => {
  return (
    <>
      {isLoading ? (
        <Spinner size="sm" />
      ) : (
        <FlatList
          data={data}
          ListHeaderComponent={() => (
            <Header
              showCheckboxes={showCheckboxes}
              headers={headers}
              setIsAllRowsSelected={setIsAllRowSelected}
              isAllRowSelected={isAllRowSelected}
              setSelectedRows={setSelectedRows}
              setExcludedRows={setExcludedRows}
            />
          )}
          renderItem={(item) => (
            <Row
              row={item?.item}
              fields={fields}
              isPressable={isPressable}
              redirectTo={redirectTo}
              showCheckboxes={showCheckboxes}
              selectedRows={selectedRows}
              isAllRowSelected={isAllRowSelected}
              setSelectedRows={setSelectedRows}
              rowIndex={item?.index}
              skip={skip}
              excludedRows={excludedRows}
              setExcludedRows={setExcludedRows}
            />
          )}
          style={{
            marginHorizontal: "15px",
            overflowX: "scroll",
            paddingBottom: "35px",
            borderBottomColor: colors.neutral.mist,
            borderBottomWidth: 1,
          }}
        />
      )}
    </>
  );
};

export default CustomTable;
